/**
 * BatchSettleDialog — one consolidated invoice across N same-route transfers.
 *
 * Shares the mental model with `TransferInvoicingDialog`'s settle form
 * but explicitly signals that freight is charged ONCE for the shared
 * truck run (that's the whole reason ops asks for this).
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Receipt, Plus, Trash, Truck } from '@phosphor-icons/react';
import { batchSettleTransfers } from '../lib/api';

export default function BatchSettleDialog({ open, onClose, transfers, onSettled }) {
  const [body, setBody] = useState({
    billing_mode: 'internal',
    bill_to_customer_name: '',
    freight_override: '',
    handling_override: '',
    custom_charges: [],
    tax_rate_pct: 0,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const route = transfers?.[0]
    ? `${transfers[0].from_warehouse_code} → ${transfers[0].to_warehouse_code}`
    : '';

  const doSettle = async () => {
    if (body.billing_mode === 'customer' && !body.bill_to_customer_name?.trim()) {
      toast.error('Enter a customer name for customer billing');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        transfer_ids: transfers.map((t) => t.id),
        billing_mode: body.billing_mode,
        bill_to_customer_name: body.billing_mode === 'customer' ? body.bill_to_customer_name : null,
        freight_override: body.freight_override === '' ? null : Number(body.freight_override),
        handling_override: body.handling_override === '' ? null : Number(body.handling_override),
        custom_charges: body.custom_charges
          .filter((c) => c.label && Number(c.amount) > 0)
          .map((c) => ({ label: c.label, amount: Number(c.amount) })),
        tax_rate_pct: Number(body.tax_rate_pct) || 0,
        notes: body.notes,
      };
      const r = await batchSettleTransfers(payload);
      toast.success(`Batch invoice ${r.data.doc_number} created — ${r.data.currency} ${Number(r.data.total).toFixed(2)}`);
      onSettled?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch settle failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="batch-settle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={20} weight="fill" /> Batch settle · {transfers?.length} transfers
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Route <span className="font-mono font-bold">{route}</span>. Freight will be charged ONCE for the shared
            truck run (using override → lane matrix → source rate card).
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-border p-2 text-xs bg-muted/30 max-h-24 overflow-y-auto" data-testid="batch-transfer-list">
            {transfers?.map((t) => (
              <div key={t.id} className="flex items-center justify-between">
                <span className="font-mono">{t.transfer_number}</span>
                <span className="text-muted-foreground">{t.items?.length || 0} items</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Billing mode</Label>
              <Select value={body.billing_mode} onValueChange={(v) => setBody({ ...body, billing_mode: v })}>
                <SelectTrigger data-testid="batch-billing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal chargeback</SelectItem>
                  <SelectItem value="customer">3PL customer invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {body.billing_mode === 'customer' && (
              <div>
                <Label className="text-xs">Bill to (customer name)</Label>
                <Input value={body.bill_to_customer_name} onChange={(e) => setBody({ ...body, bill_to_customer_name: e.target.value })} data-testid="batch-customer-name" />
              </div>
            )}
            <div>
              <Label className="text-xs">Freight override (once, blank = lane/rate card)</Label>
              <Input type="number" value={body.freight_override} onChange={(e) => setBody({ ...body, freight_override: e.target.value })} data-testid="batch-freight" />
            </div>
            <div>
              <Label className="text-xs">Handling override (blank = auto)</Label>
              <Input type="number" value={body.handling_override} onChange={(e) => setBody({ ...body, handling_override: e.target.value })} data-testid="batch-handling" />
            </div>
            <div>
              <Label className="text-xs">Tax rate %</Label>
              <Input type="number" value={body.tax_rate_pct} onChange={(e) => setBody({ ...body, tax_rate_pct: e.target.value })} data-testid="batch-tax" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Custom charges</Label>
            {body.custom_charges.map((c, i) => (
              <div key={i} className="flex gap-1 mt-1">
                <Input placeholder="Label" value={c.label} onChange={(e) => {
                  const cc = [...body.custom_charges]; cc[i] = { ...cc[i], label: e.target.value };
                  setBody({ ...body, custom_charges: cc });
                }} data-testid={`batch-custom-charge-label-${i}`} />
                <Input placeholder="Amount" type="number" className="w-32" value={c.amount} onChange={(e) => {
                  const cc = [...body.custom_charges]; cc[i] = { ...cc[i], amount: e.target.value };
                  setBody({ ...body, custom_charges: cc });
                }} data-testid={`batch-custom-charge-amount-${i}`} />
                <Button size="sm" variant="outline" onClick={() => setBody({ ...body, custom_charges: body.custom_charges.filter((_, ix) => ix !== i) })}>
                  <Trash size={12} />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="mt-1" onClick={() => setBody({ ...body, custom_charges: [...body.custom_charges, { label: '', amount: 0 }] })} data-testid="batch-add-custom-charge">
              <Plus size={12} className="mr-1" /> Add charge
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={doSettle} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="confirm-batch-settle">
            <Receipt size={14} className="mr-1" /> {saving ? 'Creating…' : 'Create batch invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
